import React, { useCallback, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import get from 'lodash/get';
import {
  ConversionStatusRenderer,
  FilePreview,
} from '@alefeducation/file-preview';
import { EllipsisV, RedoAlt } from '@alefeducation/ui-elements-library-icons';
import { Button, ThemeProvider } from '@alefeducation/ui-elements-library-core';
import {
  FlexContainer,
  PreviewContainer,
} from '../common/AssignmentPageLayout/AssignmentPageLayout';
import * as Styles from '../AssignmentSubmission/SubmissionPanel.styled';
import { isLateSubmission } from '../../utils/isLateSubmission';
import Spinner from '../common/Spinner/Spinner';
import {
  getAttachmentRenderableFileType,
  getPreviewFileURL,
  shouldCheckForFileExistance,
} from '../AssignmentSubmission/attachment.utils';
import getLocaleDate from '../../utils/getLocaleDate';
import { ToggleRender } from '../common/ToggleRender/ToggleRender';
import {
  CONVERSION_STATUS,
  useAssignmentAttachmentState,
} from '../AssignmentSubmission/AssignmentAttachmentContext';
import { useLanguage } from '../../utils/language';
import {
  isAssignmentGraded,
  isAssignmentReRequested,
} from '../../utils/assignmentStatusUtils';
import AssignmentContext from '../common/AssignmentContext/AssignmentContext';
import { LateSubmissionBubble } from '../AssignmentClassDashboard/UpdatedClassDashboard/NewAssignmentStudentsList/commonStudentSectionColumns';
import {
  useStudentSubmissionDispatch,
  useStudentSubmissionState,
} from './StudentSubmissionPageContext';
import FileUploadConfirmationModal from './FileUploadConfirmationModal';
import FileUploadWrapper from './FileUploadWrapper';
import {
  StudentCommentContainer,
  StudentUploadedFileDetails,
} from './SubmissionDetails.styled';
import AssignmentSubmissionActionsPopover from './SubmissionActionsPopover';
import RequestResubmissionModal from './RequestResubmissionModal';

export const SubmissionDetail = () => {
  const { t, i18n, dir } = useTranslation();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { attachment, conversionStatus } = useAssignmentAttachmentState();
  const [actionsPopoverButtonRef, setActionsPopoverButtonRef] = useState(null);
  const state = useStudentSubmissionState();

  const { selectedStudent } = useStudentSubmissionState();

  const hasAllTheData = state.selectedStudent && state.assignment;
  // ToDo:i18n cleanup with i18n.
  const { lang } = useLanguage();
  const submittedOn =
    get(state, 'selectedStudent.submission.submittedOn') &&
    getLocaleDate({
      date: get(state, 'selectedStudent.submission.submittedOn'),
      language: i18n.language,
    });

  const isReturned = get(state, 'selectedStudent.submission.returnedOn');
  const attachmentRequired = get(state, 'assignment.attachmentRequired');
  const dispatch = useStudentSubmissionDispatch();
  const submissionStatus = get(state, 'selectedStudent.submission.status');
  const isGraded =
    hasAllTheData &&
    get(state, 'selectedStudent.submission') &&
    isAssignmentGraded(submissionStatus);

  const uploadInputRef = useRef(null);
  const popoverClickHandler = useCallback(
    () => {
      setIsPopoverOpen(!isPopoverOpen);
    },
    [isPopoverOpen],
  );
  const actionPopoverCloseHandler = useCallback(() => {
    setIsPopoverOpen(false);
  }, []);
  const hasFileAttached = attachmentRequired && attachment;
  const isRequestResubmissionActionEnabled =
    !isGraded && !isAssignmentReRequested(submissionStatus);
  const isRequestResubmissionButtonEnabled =
    !isGraded &&
    !attachmentRequired &&
    !isAssignmentReRequested(submissionStatus);
  const showCommentPreview =
    get(state, 'selectedStudent.submission') &&
    get(state, 'selectedStudent.submission.comment');
  const isUploadActionEnabled = attachmentRequired && !isReturned;
  const showFilePreview =
    (conversionStatus === CONVERSION_STATUS.finished ||
      conversionStatus === CONVERSION_STATUS.idle) &&
    attachment &&
    attachmentRequired;
  const {
    assignmentData: { assignmentType },
  } = useContext(AssignmentContext);
  const assignmentTypeForPendo = assignmentType
    .split('_')
    .join('-')
    .toLowerCase();
  return (
    <ThemeProvider>
      <FlexContainer className="assignmentSubmission-container">
        {!hasAllTheData ? (
          <Spinner isCentered={true} testId="assignmentSpinner" />
        ) : (
          <>
            <StudentUploadedFileDetails data-testid="uploaded-file-details">
              <Styles.FileName className="usprivacy">
                {hasFileAttached && attachment.fileName}
                {hasFileAttached && (
                  <EllipsisV
                    ref={setActionsPopoverButtonRef}
                    fontSize={16}
                    onClick={popoverClickHandler}
                    data-testid={'student-assignment-actions'}
                    className={`data-analytics-${assignmentTypeForPendo}-student-submission-page-actions-menu`}
                  />
                )}
                {hasFileAttached && (
                  <AssignmentSubmissionActionsPopover
                    isActionsPopoverOpen={isPopoverOpen}
                    actionsPopoverButtonRef={actionsPopoverButtonRef}
                    attachment={attachment}
                    isReturned={!!isReturned}
                    attachmentRequired={attachmentRequired}
                    isAssignmentResubmissionEnabled={
                      isRequestResubmissionActionEnabled
                    }
                    fileNameSuffix={
                      state.selectedStudent &&
                      `_${state.selectedStudent.student.name.replace(
                        / /g,
                        '_',
                      )}`
                    }
                    closeHandler={actionPopoverCloseHandler}
                  />
                )}
              </Styles.FileName>
              <Styles.SubmittedRow>
                <Styles.SubmittedOn>
                  <span>
                    {t('Submitted on:')}
                    &nbsp;
                  </span>
                  {isLateSubmission(state.assignment.dueOn, submittedOn) ? (
                    <LateSubmissionBubble submittedOn={submittedOn} />
                  ) : (
                    <span>{submittedOn}</span>
                  )}
                </Styles.SubmittedOn>
                {isRequestResubmissionButtonEnabled && (
                  <Button
                    variant={'secondary'}
                    size={'lg'}
                    icon={<RedoAlt fontSize={16} />}
                    className={`data-analytics-${assignmentTypeForPendo}-student-submission-page-request-resubmission`}
                    onClick={() => {
                      dispatch({
                        type: 'openRequestResubmissionModal',
                        payload: {
                          studentId: selectedStudent.student.id,
                        },
                      });
                    }}
                  >
                    {t('Request Resubmission')}
                  </Button>
                )}
              </Styles.SubmittedRow>
            </StudentUploadedFileDetails>
            {showCommentPreview && (
              <ToggleRender label={t('Comments')} defaultVisible={true}>
                <StudentCommentContainer>
                  <PreviewContainer
                    className="usprivacy"
                    isSubmitted={true}
                    data-testid="comment-preview"
                    onClick={undefined}
                    dangerouslySetInnerHTML={{
                      __html: state.selectedStudent.submission.comment.replace(
                        /\n/g,
                        '<br />',
                      ),
                    }}
                  />
                </StudentCommentContainer>
              </ToggleRender>
            )}
            {isUploadActionEnabled && (
              <FileUploadWrapper ref={uploadInputRef} />
            )}

            <ConversionStatusRenderer conversionStatus={conversionStatus} />

            {showFilePreview && (
              <FilePreview
                checkForFileExistanceFirst={shouldCheckForFileExistance(
                  attachment,
                )}
                fileUrl={getPreviewFileURL(attachment)}
                fileType={getAttachmentRenderableFileType(attachment)}
                fluid={true}
                language={{ dir, lang, langCode: i18n.language }}
              />
            )}
            {state.isFileUploadConfirmationOpen && (
              <FileUploadConfirmationModal
                onConfirm={() => {
                  uploadInputRef.current.click();
                }}
              />
            )}
            {state.isRequestResubmissionModalOpen && (
              <RequestResubmissionModal isStudentSubmission={true} />
            )}
          </>
        )}
      </FlexContainer>
    </ThemeProvider>
  );
};
